import { useQuery } from '@tanstack/preact-query';
import { useContext, useState } from 'preact/hooks';
import { ApiClientContext } from '../arcticapi';
import { useTenantWebsiteBaseHref } from '../config/TenantContext';
import type { Line } from '../arcticapi/types';
import { mapLineToTimetableDetailsLine } from './timetableDetailsLine';
import type { TimetableDetailsLine } from './timetableDetailsLine';

export interface TimetableLineItem extends TimetableDetailsLine {
  via: string | null;
  operatorName: string | null;
  label: string;
}

export interface TimetableSearchState {
  query: string;
  submittedQuery: string | null;
  setQuery: (value: string) => void;
  submitSearch: () => boolean;
  submitError: string | null;
  hasSubmitted: boolean;
  loading: boolean;
  error: Error | null;
  lines: TimetableLineItem[];
}

export function useTimetableSearch(): TimetableSearchState {
  const client = useContext(ApiClientContext);
  const tenantWebsiteBaseHref = useTenantWebsiteBaseHref();
  const [query, setQuery] = useState('');
  const [submittedQuery, setSubmittedQuery] = useState<string | null>(null);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const linesQuery = useQuery<Line[], Error>({
    queryKey: ['timetable-search', submittedQuery],
    enabled: Boolean(client) && submittedQuery !== null,
    queryFn: ({ signal }) => {
      if (!client || !submittedQuery) {
        throw new Error('Timetable search request is missing.');
      }

      return client.searchLines(submittedQuery, { signal });
    },
  });

  const submitSearch = (): boolean => {
    const trimmed = query.trim();
    setSubmitError(null);

    if (!client) {
      setSubmitError(
        'Timetable search is unavailable in this preview context.',
      );
      return false;
    }

    if (!trimmed) {
      setSubmitError('Please enter a line, operator, or location to search.');
      return false;
    }

    setSubmittedQuery(trimmed);
    return true;
  };

  return {
    query,
    submittedQuery,
    setQuery,
    submitSearch,
    submitError,
    hasSubmitted: submittedQuery !== null,
    loading: Boolean(client) && submittedQuery !== null && linesQuery.isPending,
    error: linesQuery.error,
    lines: (linesQuery.data ?? []).map((line) => {
      const detailsLine = mapLineToTimetableDetailsLine(
        line,
        tenantWebsiteBaseHref,
      );

      return {
        ...detailsLine,
        via: detailsLine.via ?? null,
        operatorName: detailsLine.operatorName ?? null,
        label: detailsLine.label ?? detailsLine.name,
      };
    }),
  };
}
